// Admin "Create Member" — legacy/offline member creation bypassing self-registration.
// The member is created as PENDING PAYMENT (unpaid). An admin then records the
// payment (online/offline) and approves — only then does the member get a
// Membership ID, the certificate unlocks, and the welcome email is sent.
import React, { useState } from "react";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Copy, CheckCircle2, UserPlus } from "lucide-react";
import { DISTRICTS, LOCAL_BODY_TYPES } from "@/data/static";
import { GRAMA_PANCHAYATS_BY_DISTRICT_BLOCK, BLOCK_PANCHAYATS_BY_DISTRICT, MUNICIPALITIES_BY_DISTRICT, CORPORATIONS_BY_DISTRICT, DISTRICT_PANCHAYATS_BY_DISTRICT } from "@/data/localBodies";
import { useAuth } from "@/contexts/AuthContext";
import DatePicker from "@/components/registers/DatePicker";

const EMPTY = {
  full_name: "", email: "", mobile: "", district: "", local_body_type: "",
  local_body_name: "", block_panchayat: "", designation: "Technical Assistant",
  employee_id: "", date_of_joining: "", password: "",
};

export default function CreateMemberDialog({ open, onClose, onCreated }) {
  const { user } = useAuth();
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null); // { user, generated_password }
  const isDistrictStaff = (user?.role || "").startsWith("district_");

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...form,
        district: isDistrictStaff ? (user.district || form.district) : form.district,
      };
      const { data } = await api.post("/admin/members", payload);
      setResult(data);
      toast.success(`Member added — pending payment: ${data.user.full_name}`);
      onCreated?.(data.user);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not create member");
    } finally {
      setSaving(false);
    }
  };

  const copyCreds = () => {
    if (!result) return;
    const text = `KELTA Membership (pending approval)\nName: ${result.user.full_name}\nEmail: ${result.user.email}\nPassword: ${result.generated_password || "(set by admin)"}\nLogin: ${window.location.origin}/login`;
    navigator.clipboard.writeText(text);
    toast.success("Credentials copied");
  };

  const close = () => { setForm(EMPTY); setResult(null); onClose(); };

  // Cascading local body options — identical to the Join Membership form
  const lbType = form.local_body_type;
  const district = isDistrictStaff ? (user.district || "") : form.district;
  const lbNameSelect = (placeholder, options, testid) => (
    <Select value={form.local_body_name} onValueChange={v => set("local_body_name", v)}>
      <SelectTrigger data-testid={testid}><SelectValue placeholder={placeholder} /></SelectTrigger>
      <SelectContent>
        {(options || []).map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}
      </SelectContent>
    </Select>
  );

  return (
    <Dialog open={open} onOpenChange={v => !v && close()}>
      <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto" data-testid="create-member-dialog">
        <DialogHeader>
          <DialogTitle>Create Member (Offline / Legacy)</DialogTitle>
          <DialogDescription>
            The member is added as <strong>Pending Payment</strong>. Record the payment (Members → actions → Take Online Payment), then Approve — the Membership Certificate and the welcome email with login credentials are issued only after approval.
          </DialogDescription>
        </DialogHeader>

        {result ? (
          <div className="space-y-4" data-testid="create-member-success">
            <div className="rounded-lg border border-emerald-500/40 bg-emerald-500/5 p-4">
              <div className="flex items-center gap-2 text-emerald-700 font-semibold mb-3">
                <CheckCircle2 className="w-5 h-5" /> Member added — pending payment
              </div>
              <dl className="grid sm:grid-cols-2 gap-x-6 gap-y-1.5 text-sm">
                <div><dt className="text-xs text-muted-foreground">Name</dt><dd className="font-medium">{result.user.full_name}</dd></div>
                <div><dt className="text-xs text-muted-foreground">Status</dt><dd className="font-medium text-amber-700">Pending Payment → then Approval</dd></div>
                <div><dt className="text-xs text-muted-foreground">Login Email</dt><dd>{result.user.email}</dd></div>
                <div>
                  <dt className="text-xs text-muted-foreground">Temporary Password</dt>
                  <dd className="font-mono font-bold" data-testid="generated-password">{result.generated_password || "(the password you entered)"}</dd>
                </div>
              </dl>
              <p className="text-xs text-muted-foreground mt-3">
                Next steps: record the member&apos;s payment, then approve from the Members list. The welcome email with these credentials and the Membership ID goes out automatically on approval — no need to share manually.
              </p>
            </div>
            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={copyCreds} data-testid="copy-credentials-btn"><Copy className="w-4 h-4 mr-1.5" /> Copy credentials</Button>
              <Button variant="outline" onClick={() => { setResult(null); setForm(EMPTY); }} data-testid="create-another-btn">
                <UserPlus className="w-4 h-4 mr-1.5" /> Add another
              </Button>
              <Button onClick={close} className="bg-brand-blue hover:bg-brand-blue/90 text-white" data-testid="create-member-done">Done</Button>
            </DialogFooter>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4">
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-semibold">Full Name *</Label>
                <Input required value={form.full_name} onChange={e => set("full_name", e.target.value)} data-testid="cm-full-name" />
              </div>
              <div>
                <Label className="text-xs font-semibold">Email *</Label>
                <Input required type="email" value={form.email} onChange={e => set("email", e.target.value)} data-testid="cm-email" />
              </div>
              <div>
                <Label className="text-xs font-semibold">Mobile</Label>
                <Input value={form.mobile} onChange={e => set("mobile", e.target.value)} data-testid="cm-mobile" />
              </div>
              <div>
                <Label className="text-xs font-semibold">District * {isDistrictStaff && <span className="text-muted-foreground font-normal">(locked to yours)</span>}</Label>
                {isDistrictStaff ? (
                  <Input value={district} disabled data-testid="cm-district" />
                ) : (
                  <Select value={form.district} onValueChange={v => setForm(f => ({ ...f, district: v, block_panchayat: "", local_body_name: "" }))} required>
                    <SelectTrigger data-testid="cm-district"><SelectValue placeholder="Select district" /></SelectTrigger>
                    <SelectContent>
                      {DISTRICTS.map(d => <SelectItem key={d.slug} value={d.name}>{d.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                )}
              </div>
              <div>
                <Label className="text-xs font-semibold">Local Body Type *</Label>
                <Select value={form.local_body_type} onValueChange={v => setForm(f => ({ ...f, local_body_type: v, block_panchayat: "", local_body_name: "" }))} required>
                  <SelectTrigger data-testid="cm-lb-type"><SelectValue placeholder="Select type" /></SelectTrigger>
                  <SelectContent>
                    {LOCAL_BODY_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              {lbType === "Grama Panchayat" && (
                <div>
                  <Label className="text-xs font-semibold">Block Panchayat *</Label>
                  <Select value={form.block_panchayat} onValueChange={v => setForm(f => ({ ...f, block_panchayat: v, local_body_name: "" }))}>
                    <SelectTrigger data-testid="cm-block"><SelectValue placeholder="Select Block" /></SelectTrigger>
                    <SelectContent>
                      {(BLOCK_PANCHAYATS_BY_DISTRICT[district] || []).map(b => <SelectItem key={b} value={b}>{b}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}
              {lbType && lbType !== "Grama Panchayat" && (
                <div>
                  <Label className="text-xs font-semibold">Local Body Name *</Label>
                  {lbType === "Block Panchayat"
                    ? lbNameSelect("Select Block Panchayat", BLOCK_PANCHAYATS_BY_DISTRICT[district], "cm-lb-name")
                    : lbType === "Municipality"
                    ? lbNameSelect("Select Municipality", MUNICIPALITIES_BY_DISTRICT[district], "cm-lb-name")
                    : lbType === "Corporation"
                    ? lbNameSelect("Select Corporation", CORPORATIONS_BY_DISTRICT[district], "cm-lb-name")
                    : lbNameSelect("Select District Panchayat", DISTRICT_PANCHAYATS_BY_DISTRICT[district], "cm-lb-name")}
                </div>
              )}
              {lbType === "Grama Panchayat" && (
                <div>
                  <Label className="text-xs font-semibold">Grama Panchayat *</Label>
                  <Select value={form.local_body_name} onValueChange={v => set("local_body_name", v)} disabled={!form.block_panchayat}>
                    <SelectTrigger data-testid="cm-lb-name"><SelectValue placeholder={form.block_panchayat ? "Select Grama Panchayat" : "Select Block Panchayat first"} /></SelectTrigger>
                    <SelectContent>
                      {((GRAMA_PANCHAYATS_BY_DISTRICT_BLOCK[district] || {})[form.block_panchayat] || []).map(gp => <SelectItem key={gp} value={gp}>{gp}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div>
                <Label className="text-xs font-semibold">Designation</Label>
                <Input value={form.designation} onChange={e => set("designation", e.target.value)} data-testid="cm-designation" />
              </div>
              <div>
                <Label className="text-xs font-semibold">Employee ID</Label>
                <Input value={form.employee_id} onChange={e => set("employee_id", e.target.value)} data-testid="cm-employee-id" />
              </div>
              <div>
                <Label className="text-xs font-semibold">Date of Joining <span className="text-muted-foreground font-normal">(type DD-MM-YYYY or pick)</span></Label>
                <DatePicker value={form.date_of_joining} onChange={v => set("date_of_joining", v)} testid="cm-doj" />
              </div>
              <div>
                <Label className="text-xs font-semibold">Password <span className="text-muted-foreground font-normal">(blank = auto-generate)</span></Label>
                <Input value={form.password} onChange={e => set("password", e.target.value)} placeholder="Leave blank to auto-generate" data-testid="cm-password" />
              </div>
            </div>
            <DialogFooter className="gap-2">
              <Button type="button" variant="outline" onClick={close}>Cancel</Button>
              <Button type="submit" disabled={saving} className="bg-brand-blue hover:bg-brand-blue/90 text-white" data-testid="cm-submit">
                <UserPlus className="w-4 h-4 mr-1.5" /> {saving ? "Creating…" : "Create Member"}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
